/**
 * MetaCarouselMockup - Facebook/Instagram 1:1 Carousel Ad Mockup
 *
 * Dimensions: 420px width (matching inbeat.co reference)
 *
 * Structure (matching Facebook/Meta carousel ad format):
 * - Header: Round profile picture (43px), page name, "Sponsored" text, three-dot menu
 * - Caption: Text description
 * - Carousel: Horizontally scrolling cards (360px each) with:
 *   - 1:1 image
 *   - Headline + description section
 *   - CTA button per card
 * - Navigation: Left/right arrows on hover
 * - Engagement Section: Like/love/haha reactions, engagement counts
 * - Action Buttons: Like, Comment, Share
 *
 * Usage:
 *   <MetaCarouselMockup
 *     pageName="Ford"
 *     caption="Discover the all-new Ford Mustang..."
 *     items={[
 *       { imageUrl: '/img1.jpg', headline: 'Mustang GT', description: 'Unleash power' },
 *       { imageUrl: '/img2.jpg', headline: 'Mach-E', description: 'Electric future' },
 *     ]}
 *     ctaText="Shop Now"
 *   />
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { MediaDisplay } from './MediaDisplay';

interface CarouselItem {
  imageUrl?: string;
  contentType?: string | null;
  headline?: string;
  description?: string;
  cta?: string;
}

interface MetaCarouselMockupProps {
  items: CarouselItem[];
  caption?: string;
  ctaText?: string;
  profilePicUrl?: string;
  pageName?: string;
  activeIndex?: number;
  onVisibleCardChange?: (index: number) => void;
}

export function MetaCarouselMockup({
  items,
  caption = 'Primary text placeholder\u2026',
  ctaText = 'Shop Now',
  profilePicUrl,
  pageName = 'Ford',
  activeIndex,
  onVisibleCardChange,
}: MetaCarouselMockupProps) {
  const [currentIndex, setCurrentIndex] = useState(activeIndex ?? 0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScroll = useRef(false);

  // Sync from parent (e.g. clicking a card in the details panel)
  const isSyncingFromParent = useRef(false);

  useEffect(() => {
    if (activeIndex !== undefined && activeIndex !== currentIndex) {
      isSyncingFromParent.current = true;
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to currentIndex and notify parent whenever it changes
  useEffect(() => {
    if (!carouselRef.current) return;
    isProgrammaticScroll.current = true;
    carouselRef.current.scrollTo({ left: currentIndex * 370, behavior: 'smooth' });
    const timer = setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 600);
    if (isSyncingFromParent.current) {
      isSyncingFromParent.current = false;
    } else {
      onVisibleCardChange?.(currentIndex);
    }
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Handle manual drag/touchpad scroll
  const handleScroll = useCallback(() => {
    if (isProgrammaticScroll.current) return;
    if (!carouselRef.current) return;
    const scrollLeft = carouselRef.current.scrollLeft;
    const index = Math.round(Math.max(0, scrollLeft - 14) / 370);
    setCurrentIndex(Math.min(index, items.length - 1));
  }, [items.length]);

  const scrollCarouselLeft = () => setCurrentIndex((prev) => Math.max(0, prev - 1));
  const scrollCarouselRight = () => setCurrentIndex((prev) => Math.min(items.length - 1, prev + 1));

  return (
    <div
      className="bg-white border border-[#e5e5e5] rounded-lg mx-auto"
      style={{ maxWidth: '400px', fontFamily: '"Segoe UI", sans-serif' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between" style={{ padding: '18px 14px 6px 14px' }}>
        <div className="flex items-center" style={{ gap: '9px' }}>
          <div
            className="rounded-full bg-blue-600 flex items-center justify-center text-white font-bold overflow-hidden"
            style={{ width: '43px', height: '43px' }}
          >
            {profilePicUrl ? (
              <img src={profilePicUrl} alt={pageName} className="w-full h-full object-cover" />
            ) : (
              pageName.charAt(0)
            )}
          </div>
          <div>
            <h4
              className="font-semibold"
              style={{ fontSize: '16px', lineHeight: '22px', color: '#050505', marginBottom: 0 }}
            >
              {pageName}
            </h4>
            <div
              className="flex items-center"
              style={{ fontSize: '13px', lineHeight: '18px', color: '#606770', gap: '3px' }}
            >
              Sponsored
              <span
                style={{
                  height: '2px',
                  width: '2px',
                  borderRadius: '50%',
                  backgroundColor: '#616770',
                }}
              ></span>
              <svg
                width="13"
                height="14"
                viewBox="0 0 13 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.5 0.5C7.65323 0.5 8.75403 0.814516 9.75 1.39113C10.746 1.96774 11.5323 2.75403 12.1089 3.75C12.6855 4.74597 13 5.84677 13 7C13 8.17944 12.6855 9.25403 12.1089 10.25C11.5323 11.246 10.746 12.0585 9.75 12.6351C8.75403 13.2117 7.65323 13.5 6.5 13.5C5.32056 13.5 4.24597 13.2117 3.25 12.6351C2.25403 12.0585 1.44153 11.246 0.864919 10.25C0.288306 9.25403 0 8.17944 0 7C0 5.84677 0.288306 4.74597 0.864919 3.75C1.44153 2.75403 2.25403 1.96774 3.25 1.39113C4.24597 0.814516 5.32056 0.5 6.5 0.5Z"
                  fill="#606770"
                />
              </svg>
            </div>
          </div>
        </div>
        <button>
          <svg width="15" height="4" viewBox="0 0 15 4" fill="none">
            <path
              d="M1.92496 0.728516C2.35261 0.728516 2.76275 0.898398 3.06515 1.20079C3.36754 1.50319 3.53742 1.91332 3.53742 2.34097C3.53742 2.76862 3.36754 3.17876 3.06515 3.48115C2.76275 3.78355 2.35261 3.95343 1.92496 3.95343C1.49731 3.95343 1.08716 3.78355 0.784768 3.48115C0.482374 3.17876 0.3125 2.76862 0.3125 2.34097C0.3125 1.91332 0.482374 1.50319 0.784768 1.20079C1.08716 0.898398 1.49731 0.728516 1.92496 0.728516ZM7.29982 0.728516C7.72747 0.728516 8.13761 0.898398 8.44001 1.20079C8.7424 1.50319 8.91227 1.91332 8.91227 2.34097C8.91227 2.76862 8.7424 3.17876 8.44001 3.48115C8.13761 3.78355 7.72747 3.95343 7.29982 3.95343C6.87217 3.95343 6.46202 3.78355 6.15963 3.48115C5.85723 3.17876 5.68736 2.76862 5.68736 2.34097C5.68736 1.91332 5.85723 1.50319 6.15963 1.20079C6.46202 0.898398 6.87217 0.728516 7.29982 0.728516ZM12.6747 0.728516C13.1023 0.728516 13.5125 0.898398 13.8149 1.20079C14.1173 1.50319 14.2871 1.91332 14.2871 2.34097C14.2871 2.76862 14.1173 3.17876 13.8149 3.48115C13.5125 3.78355 13.1023 3.95343 12.6747 3.95343C12.247 3.95343 11.8369 3.78355 11.5345 3.48115C11.2321 3.17876 11.0622 2.76862 11.0622 2.34097C11.0622 1.91332 11.2321 1.50319 11.5345 1.20079C11.8369 0.898398 12.247 0.728516 12.6747 0.728516Z"
              fill="#262627"
            />
          </svg>
        </button>
      </div>

      {/* Caption */}
      <div
        style={{
          fontSize: '15px',
          lineHeight: '20px',
          letterSpacing: '0.005em',
          fontWeight: 400,
          color: '#050505',
          padding: '2px 14px 12px 14px',
          minHeight: '40px',
          overflowWrap: 'break-word',
        }}
      >
        {caption}
      </div>

      {/* Carousel - Horizontally Scrolling Cards */}
      <div className="relative group">
        <div
          ref={carouselRef}
          className="overflow-x-scroll overflow-y-hidden flex"
          onScroll={handleScroll}
          style={{
            gap: '10px',
            paddingBottom: '2px',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {items.map((item, idx) => (
            <div
              key={idx}
              data-card-index={idx}
              className="flex-shrink-0 border border-[#e5e5e5] overflow-hidden flex flex-col"
              style={{
                width: '360px',
                borderRadius: '11px',
                marginLeft: idx === 0 ? '14px' : '0',
                marginRight: idx === items.length - 1 ? '14px' : '0',
              }}
            >
              {/* Card Image/Video */}
              <div
                className="w-full bg-gray-200 flex items-center justify-center"
                style={{ aspectRatio: '1/1' }}
              >
                {item.imageUrl ? (
                  <MediaDisplay
                    url={item.imageUrl}
                    contentType={item.contentType}
                    alt={item.headline}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="text-center text-gray-400">
                    <div className="text-4xl font-bold mb-2">1:1 or 4:5</div>
                    <div className="text-xs">1080x1080 or 1080x1350px recommended</div>
                  </div>
                )}
              </div>
              {/* Card CTA Section */}
              <div className="w-full border-t border-[#e5e5e5]">
                <div
                  className="bg-white flex items-center justify-between"
                  style={{ padding: '10px 12px 8px 12px', gap: '4px' }}
                >
                  <div className="flex-1">
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '18px',
                        lineHeight: '20px',
                        color: '#050505',
                      }}
                    >
                      {item.headline || 'Headline'}
                    </div>
                    <div
                      style={{
                        fontWeight: 350,
                        fontSize: '12px',
                        lineHeight: '20px',
                        color: '#050505',
                        marginTop: '4px',
                      }}
                    >
                      {item.description || 'Description'}
                    </div>
                  </div>
                  <button
                    className="bg-[#e4e6eb] rounded-[5px] text-[#050505] whitespace-nowrap"
                    style={{
                      fontWeight: 600,
                      fontSize: '15px',
                      lineHeight: '16px',
                      padding: '12px',
                    }}
                  >
                    {item.cta || ctaText}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Show on Hover */}
        <button
          onClick={scrollCarouselLeft}
          className="absolute opacity-0 group-hover:opacity-100 transition-all"
          style={{
            left: '14px',
            top: '40%',
            transform: 'translateY(-50%)',
            width: '49px',
            height: '49px',
            borderRadius: '50%',
            background: '#fff',
            border: '1px solid #cfd0d4',
            color: '#66676b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            cursor: 'pointer',
          }}
        >
          <span>&#10094;</span>
        </button>
        <button
          onClick={scrollCarouselRight}
          className="absolute opacity-0 group-hover:opacity-100 transition-all"
          style={{
            right: '14px',
            top: '40%',
            transform: 'translateY(-50%)',
            width: '49px',
            height: '49px',
            borderRadius: '50%',
            background: '#fff',
            border: '1px solid #cfd0d4',
            color: '#66676b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            cursor: 'pointer',
          }}
        >
          <span>&#10095;</span>
        </button>
      </div>

      {/* Engagement Section */}
      <div
        className="flex items-center justify-between border-b border-[#dadde1]"
        style={{ padding: '12px 0px', margin: '0px 14px' }}
      >
        <div className="flex items-center" style={{ gap: '9px' }}>
          <div className="flex">
            <img
              src="/icons/facebook-like.svg"
              alt="Like"
              className="w-[18px] h-[18px]"
              style={{ marginLeft: '0' }}
            />
            <img
              src="/icons/facebook-love.svg"
              alt="Love"
              className="w-[18px] h-[18px]"
              style={{ marginLeft: '-4px' }}
            />
            <img
              src="/icons/facebook-haha.svg"
              alt="Haha"
              className="w-[18px] h-[18px]"
              style={{ marginLeft: '-4px' }}
            />
          </div>
          <div style={{ fontWeight: 400, fontSize: '15px', lineHeight: '17px', color: '#66676b' }}>
            Joe Soap and 15 other
          </div>
        </div>
        <div style={{ fontWeight: 400, fontSize: '15px', lineHeight: '17px', color: '#66676b' }}>
          1 comment 1 share
        </div>
      </div>

      {/* Action Buttons */}
      <div
        className="flex items-center justify-between"
        style={{ padding: '10px 55px 12px', alignItems: 'center' }}
      >
        <button
          className="flex items-center"
          style={{
            gap: '6px',
            fontWeight: 500,
            fontSize: '15px',
            lineHeight: '18px',
            color: '#66676b',
          }}
        >
          <svg width="19" height="18" viewBox="0 0 19 18" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18 10.3282C18 9.7635 17.6875 9.27018 17.2228 9.00309C17.2132 8.9975 17.2093 8.98417 17.2185 8.97729C17.5869 8.69601 17.8252 8.25903 17.8252 7.76657C17.8252 6.91757 17.1214 6.22899 16.2529 6.22899H11.4478C12.3307 1.70913 10.105 0.810231 9.2181 1.03044C8.68833 1.16162 8.63194 1.01668 8.7281 2.04288L8.77356 3.74089C8.77793 3.91078 8.75695 4.08411 8.70668 4.25615C8.65642 4.42818 8.58036 4.58603 8.48463 4.72796L6.19551 8.10334V16.4768L14.5932 16.476C15.4206 16.4308 16.078 15.7594 16.078 14.9397C16.078 14.6928 16.0173 14.4601 15.9115 14.2537C15.9071 14.2447 15.9089 14.2326 15.9189 14.2309C16.6546 14.1023 17.2136 13.4756 17.2136 12.7195C17.2136 12.3776 17.0982 12.0632 16.9055 11.8077C16.9007 11.8021 16.9037 11.7935 16.9112 11.7914C17.5428 11.5918 18 11.0121 18 10.3282ZM1 17H6.19551V6.47478H1V17Z"
              stroke="#66676B"
              strokeLinejoin="round"
            />
          </svg>
          <span>Like</span>
        </button>
        <button
          className="flex items-center"
          style={{
            gap: '6px',
            fontWeight: 500,
            fontSize: '15px',
            lineHeight: '18px',
            color: '#66676b',
          }}
        >
          <svg width="17" height="19" viewBox="0 0 17 19" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4 14.0001H4.96491V17L8.92982 14.0001H13C14.6569 14.0001 16 12.657 16 11.0001V4C16 2.34315 14.6569 1 13 1H4C2.34315 1 1 2.34315 1 4V11.0001C1 12.657 2.34315 14.0001 4 14.0001Z"
              stroke="#66676B"
            />
          </svg>
          <span>Comment</span>
        </button>
        <button
          className="flex items-center"
          style={{
            gap: '6px',
            fontWeight: 500,
            fontSize: '15px',
            lineHeight: '18px',
            color: '#66676b',
          }}
        >
          <svg width="17" height="16" viewBox="0 0 17 16" fill="none">
            <path
              d="M15.5 8L12.5 11V9H9.5C8.96957 9 8.46086 9.21071 8.08579 9.58579C7.71071 9.96086 7.5 10.4696 7.5 11V11.5H6.5V11C6.5 10.2044 6.81607 9.44129 7.37868 8.87868C7.94129 8.31607 8.70435 8 9.5 8H12.5V6L15.5 8Z"
              stroke="#66676B"
              strokeLinejoin="round"
            />
            <path
              d="M11.5 4H4.5C3.96957 4 3.46086 4.21071 3.08579 4.58579C2.71071 4.96086 2.5 5.46957 2.5 6V12C2.5 12.5304 2.71071 13.0391 3.08579 13.4142C3.46086 13.7893 3.96957 14 4.5 14H6"
              stroke="#66676B"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Share</span>
        </button>
      </div>
    </div>
  );
}
